





























































import ReserveDialog from '@/components/reserves/ReserveDialog.vue';
import IReservesApiModel from '@/model/api/reserves/IReservesApiModel';
import container from '@/model/ModelContainer';
import ISocketIOModel from '@/model/socketio/ISocketIOModel';
import IReserveStateUtil, { ReserveStateData } from '@/model/state/reserve/IReserveStateUtil';
import ISnackbarState from '@/model/state/snackbar/ISnackbarState';
import { ISettingStorageModel } from '@/model/storage/setting/ISettingStorageModel';
import { Component, Vue } from 'vue-property-decorator';
import * as apid from '../../../../api';

const SLOT_MIN = 30; // 30 分刻み
const ROW_PX = 30; // 30 分 = 30px
const DAY_HEADER_PX = 30; // 日付ヘッダ band の高さ (band 26px + 余白 4px)
const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

interface TimeSlot {
    label: string;
    isHour: boolean;
    isDayBreak: boolean;
    dayLabel: string;
    topPx: number; // この slot の絶対 top (前にある day-header の累積 px を含む)
    headerOffsetBeforePx: number; // この slot の手前にあるべき day-header の top (この slot が dayBreak のときのみ有効)
}
interface Block {
    reserveId: number;
    name: string;
    channelName: string;
    channelType: string;
    timeRange: string;
    topPx: number;
    heightPx: number;
    isRecording: boolean;
}

@Component({
    components: { ReserveDialog },
})
export default class TunerTimelineView extends Vue {
    public isOpenDialog: boolean = false;
    public dialogReserve: ReserveStateData | null = null;
    public loading: boolean = true;
    public data: apid.TunerAllocations | null = null;
    public timeSlots: TimeSlot[] = [];
    public blocksByTuner: { [key: number]: Block[] } = {};
    public rowPx: number = ROW_PX;
    public dayHeaderPx: number = DAY_HEADER_PX;
    private totalHeaderPx: number = 0;

    private reservesApiModel: IReservesApiModel = container.get<IReservesApiModel>('IReservesApiModel');
    private reserveStateUtil: IReserveStateUtil = container.get<IReserveStateUtil>('IReserveStateUtil');
    private setting: ISettingStorageModel = container.get<ISettingStorageModel>('ISettingStorageModel');
    private socketIoModel: ISocketIOModel = container.get<ISocketIOModel>('ISocketIOModel');
    private snackbarState: ISnackbarState = container.get<ISnackbarState>('ISnackbarState');
    private startEpochMin: number = 0;

    private onUpdate = (async (): Promise<void> => {
        await this.fetch();
    }).bind(this);

    public async created(): Promise<void> {
        await this.fetch();
        this.socketIoModel.onUpdateState(this.onUpdate);
    }

    public beforeDestroy(): void {
        this.socketIoModel.offUpdateState(this.onUpdate);
    }

    get trackHeightPx(): number {
        return this.timeSlots.length * ROW_PX + this.totalHeaderPx;
    }

    get dayBreakSlots(): Array<{ index: number; dayLabel: string; topPx: number }> {
        const result: Array<{ index: number; dayLabel: string; topPx: number }> = [];
        this.timeSlots.forEach((s, i) => {
            // band は対象 slot の topPx から DAY_HEADER_PX 分だけ上に置く (slot 本体と重ならない位置)
            if (s.isDayBreak) result.push({ index: i, dayLabel: s.dayLabel, topPx: s.topPx - DAY_HEADER_PX });
        });
        return result;
    }

    get headerHeightPx(): number {
        return 44; // .header の height と一致
    }

    private async fetch(): Promise<void> {
        try {
            const isHalf = this.setting.getSavedValue().isHalfWidthDisplayed;
            const data = await this.reservesApiModel.getTunerAllocations(isHalf);
            this.data = data;
            this.buildTimeline(data);
        } catch (err) {
            this.snackbarState.open({ color: 'error', text: 'チューナー情報の取得に失敗' });
            console.error(err);
        } finally {
            this.loading = false;
        }
    }

    private buildTimeline(data: apid.TunerAllocations): void {
        const now = new Date();
        const startMs = Math.floor(now.getTime() / (SLOT_MIN * 60 * 1000)) * (SLOT_MIN * 60 * 1000);
        let endMs = startMs + SLOT_MIN * 60 * 1000;
        for (const a of data.allocations) {
            if (a.endAt > endMs) endMs = a.endAt;
        }
        endMs = Math.ceil(endMs / (SLOT_MIN * 60 * 1000)) * (SLOT_MIN * 60 * 1000);

        this.startEpochMin = startMs / 60000;
        const endEpochMin = endMs / 60000;
        const slotsCount = Math.max(1, (endEpochMin - this.startEpochMin) / SLOT_MIN);

        const slots: TimeSlot[] = [];
        let lastDayKey = '';
        let cumulativeHeaderPx = 0;
        for (let i = 0; i < slotsCount; i++) {
            const m = this.startEpochMin + i * SLOT_MIN;
            const d = new Date(m * 60000);
            const hh = d.getHours();
            const mm = d.getMinutes();
            const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const isDayBreak = dayKey !== lastDayKey;
            lastDayKey = dayKey;
            const headerOffsetBefore = cumulativeHeaderPx; // この slot の手前にあるべき header の top
            if (isDayBreak) cumulativeHeaderPx += DAY_HEADER_PX;
            slots.push({
                label: `${this.zeroPad(hh)}:${this.zeroPad(mm)}`,
                isHour: mm === 0,
                isDayBreak,
                dayLabel: this.fmtFullDate(d),
                topPx: i * ROW_PX + cumulativeHeaderPx,
                headerOffsetBeforePx: headerOffsetBefore,
            });
        }
        this.timeSlots = slots;
        this.totalHeaderPx = cumulativeHeaderPx;

        const nowMs = now.getTime();
        const buckets: { [key: number]: Block[] } = {};
        for (const a of data.allocations) {
            const startMin = a.startAt / 60000;
            const endMin = a.endAt / 60000;
            if (endMin <= this.startEpochMin) continue;
            const clampedStart = Math.max(startMin, this.startEpochMin);
            const isRecording = a.startAt <= nowMs && nowMs < a.endAt;
            const startTop = this.timeMinToTopPx(clampedStart, slots);
            const endTop = this.timeMinToTopPx(endMin, slots);
            const block: Block = {
                reserveId: a.reserveId,
                name: a.name,
                channelName: a.channelName,
                channelType: a.channelType,
                timeRange: this.formatTimeRange(a.startAt, a.endAt, nowMs),
                topPx: startTop,
                heightPx: Math.max(ROW_PX / 2, endTop - startTop),
                isRecording,
            };
            if (typeof buckets[a.tunerIndex] === 'undefined') buckets[a.tunerIndex] = [];
            buckets[a.tunerIndex].push(block);
        }
        this.blocksByTuner = buckets;
    }

    /** ブロックの番組名クリック時: API から ReserveItem を取り直して ReserveDialog を表示 */
    public async onClickName(reserveId: number): Promise<void> {
        try {
            const isHalf = this.setting.getSavedValue().isHalfWidthDisplayed;
            const item = await this.reservesApiModel.get(reserveId, isHalf);
            const stateDatas = this.reserveStateUtil.convertReserveItemsToStateDatas([item], isHalf);
            if (stateDatas.length === 0) return;
            this.dialogReserve = stateDatas[0];
            this.isOpenDialog = true;
        } catch (err) {
            this.snackbarState.open({ color: 'error', text: '予約情報の取得に失敗' });
            console.error(err);
        }
    }

    /** 任意の時刻 (epoch min) をトラック上の top px に変換。slot 内は分数比例で配置する。 */
    private timeMinToTopPx(timeMin: number, slots: TimeSlot[]): number {
        if (slots.length === 0) return 0;
        const offsetMin = timeMin - this.startEpochMin;
        const idx = Math.floor(offsetMin / SLOT_MIN);
        if (idx <= 0) return slots[0].topPx;
        if (idx >= slots.length) {
            const last = slots[slots.length - 1];
            return last.topPx + ROW_PX;
        }
        const slot = slots[idx];
        const intra = offsetMin - idx * SLOT_MIN;
        return slot.topPx + (intra / SLOT_MIN) * ROW_PX;
    }

    /** "hh:mm-hh:mm (xx分後 / x時間x分後)" — 録画中は (あと xx 分) を表示 */
    private formatTimeRange(startAt: number, endAt: number, nowMs: number): string {
        const head = `${this.fmtHM(startAt)}-${this.fmtHM(endAt)}`;
        if (startAt <= nowMs && nowMs < endAt) {
            const remainMin = Math.max(0, Math.ceil((endAt - nowMs) / 60000));
            return `${head} (あと${remainMin}分)`;
        }
        const diffMin = Math.floor((startAt - nowMs) / 60000);
        if (diffMin <= 0) return head;
        if (diffMin < 60) return `${head} (${diffMin}分後)`;
        const h = Math.floor(diffMin / 60);
        const m = diffMin % 60;
        return m === 0 ? `${head} (${h}時間後)` : `${head} (${h}時間${m}分後)`;
    }

    private fmtFullDate(d: Date): string {
        return `${d.getFullYear()}/${this.zeroPad(d.getMonth() + 1)}/${this.zeroPad(d.getDate())}（${DAY_LABELS[d.getDay()]}）`;
    }

    private zeroPad(n: number): string {
        return `0${n}`.slice(-2);
    }

    private fmtHM(ms: number): string {
        const d = new Date(ms);
        return `${this.zeroPad(d.getHours())}:${this.zeroPad(d.getMinutes())}`;
    }
}
